import React, { useEffect, useRef, useState } from 'react';
import { referralChannelAdvancedOptions } from 'types';
import {
  ExpandableTile,
  TileAboveTheFoldContent,
  TileBelowTheFoldContent,
  NumberInput,
  FormGroup,
  RadioButtonGroup,
  RadioButton,
  TextArea,
  Link
} from 'carbon-components-react';
import Utils from 'utilities/helper.util';

const MAX_AREA_STEP = 0.1;
const MAX_AREA_MAX = 19;
const MAX_AREA_MIN = 0.1;

const AdvancedFeaturesTile: React.FC<Props> = ({
  advancedOptions,
  setAdvancedOptionsInvavid,
  setAdvancedOptionsDirty
}) => {
  const tileContentRef = useRef<HTMLDivElement>(null);
  const [maxAreaValue, setMaxAreaValue] = useState<number>(
    advancedOptions?.current?.maxEnquiryArea || MAX_AREA_STEP
  );
  const [splitEnquiries, setSplitEnquiries] = useState<boolean>(
    advancedOptions?.current?.splitEnquiries || false
  );
  const [isMaxAreaInvalid, setIsMaxAreaInvalid] = useState<boolean>(
    advancedOptions?.current?.isMaxAreaInvalid || false
  );
  const [frequency, setFrequency] = useState<string>(
    advancedOptions?.current?.referralSummaryFrequency || 'NEVER'
  );
  const [isExpandPanel, setIsExpandPanel] = useState<boolean>(
    advancedOptions?.current?.isExpandPanel || false
  );
  const [summaryEmails, setSummaryEmails] = useState<string>(
    advancedOptions?.current?.referralSummaryEmail || ''
  );
  const [isSummaryEmailsInvalid, setIsSummaryEmailsInvalid] = useState<boolean>(
    advancedOptions?.current?.isSummaryEmailsInvalid || false
  );
  const [isSummaryEmailsDirty, setIsSummaryEmailsDirty] = useState<boolean>(
    false
  );

  const handleNumberInputChange = (event: any) => {
    const newValue = Number(event.imaginaryTarget.value);
    if (newValue > 0) {
      setIsMaxAreaInvalid(false);
      setMaxAreaValue(Number(newValue.toFixed(1)));
    } else {
      setIsMaxAreaInvalid(true);
      setMaxAreaValue(0);
    }
    typeof setAdvancedOptionsDirty === 'function' &&
      setAdvancedOptionsDirty(true);
  };

  const handleMaxAreaModeChange = (mode: string) => {
    typeof setAdvancedOptionsDirty === 'function' &&
      setAdvancedOptionsDirty(true);
    setSplitEnquiries(mode !== 'none');
    if (advancedOptions?.current) {
      advancedOptions.current.maxEnquiryArea = maxAreaValue;
    } else {
      advancedOptions.current = {
        maxEnquiryArea: maxAreaValue
      };
    }
  };

  const handleFrequencyChange = (value: string) => {
    typeof setAdvancedOptionsDirty === 'function' &&
      setAdvancedOptionsDirty(true);
    setFrequency(value);
  };

  const handleSummaryEmailsChange = (event: any) => {
    const newValue: string = event.target.value?.replaceAll(';', ',');
    const emails = newValue.split(',');
    const valids = emails.map((email) => !Utils.isInValidEmail(email));
    setIsSummaryEmailsInvalid(valids.includes(false));
    setSummaryEmails(newValue);
    setIsSummaryEmailsDirty(true);
    typeof setAdvancedOptionsDirty === 'function' &&
      setAdvancedOptionsDirty(true);
  };

  useEffect(() => {
    if (advancedOptions?.current) {
      advancedOptions.current.maxEnquiryArea = maxAreaValue;
    } else {
      advancedOptions.current = {
        maxEnquiryArea: maxAreaValue
      };
    }
  }, [maxAreaValue, advancedOptions]);

  useEffect(() => {
    if (advancedOptions?.current) {
      advancedOptions.current.referralSummaryFrequency = frequency;
    } else {
      advancedOptions.current = {
        referralSummaryFrequency: frequency
      };
    }
  }, [frequency, advancedOptions]);

  useEffect(() => {
    setAdvancedOptionsInvavid(
      (advancedOptions?.current?.isMaxAreaInvalid &&
        advancedOptions?.current?.splitEnquiries) ||
        (advancedOptions?.current?.isSummaryEmailsInvalid &&
          advancedOptions.current.referralSummaryFrequency !== 'NEVER')
    );
    advancedOptions?.current &&
      advancedOptions.current.referralSummaryEmail === '' &&
      setIsSummaryEmailsInvalid(true);
  }, [advancedOptions, setAdvancedOptionsInvavid]);

  useEffect(() => {
    if (advancedOptions?.current) {
      advancedOptions.current.referralSummaryEmail = summaryEmails;
    } else {
      advancedOptions.current = {
        referralSummaryEmail: summaryEmails
      };
    }
  }, [summaryEmails, advancedOptions]);

  useEffect(() => {
    if (advancedOptions?.current) {
      advancedOptions.current.isSummaryEmailsInvalid = isSummaryEmailsInvalid;
    } else {
      advancedOptions.current = {
        isSummaryEmailsInvalid: isSummaryEmailsInvalid
      };
    }
    setAdvancedOptionsInvavid(
      (advancedOptions?.current?.isMaxAreaInvalid &&
        advancedOptions?.current?.splitEnquiries) ||
        (isSummaryEmailsInvalid &&
          advancedOptions?.current?.referralSummaryFrequency &&
          advancedOptions.current.referralSummaryFrequency !== 'NEVER')
    );
  }, [isSummaryEmailsInvalid, advancedOptions, setAdvancedOptionsInvavid]);

  useEffect(() => {
    if (advancedOptions?.current) {
      advancedOptions.current.isMaxAreaInvalid = isMaxAreaInvalid;
    } else {
      advancedOptions.current = {
        isMaxAreaInvalid: isMaxAreaInvalid
      };
    }
    setAdvancedOptionsInvavid(
      (isMaxAreaInvalid && advancedOptions?.current?.splitEnquiries) ||
        (advancedOptions?.current?.isSummaryEmailsInvalid &&
          advancedOptions?.current?.referralSummaryFrequency &&
          advancedOptions.current.referralSummaryFrequency !== 'NEVER')
    );
  }, [isMaxAreaInvalid, advancedOptions, setAdvancedOptionsInvavid]);

  useEffect(() => {
    if (advancedOptions?.current) {
      advancedOptions.current.splitEnquiries =
        typeof advancedOptions?.current?.splitEnquiries === 'boolean'
          ? splitEnquiries
          : advancedOptions?.current?.splitEnquiries !== undefined;
    } else {
      advancedOptions.current = {
        splitEnquiries: splitEnquiries
      };
    }
    setAdvancedOptionsInvavid(
      (advancedOptions?.current?.isMaxAreaInvalid && splitEnquiries) ||
        (advancedOptions?.current?.isSummaryEmailsInvalid &&
          advancedOptions?.current?.referralSummaryFrequency &&
          advancedOptions.current.referralSummaryFrequency !== 'NEVER')
    );
  }, [splitEnquiries, advancedOptions, setAdvancedOptionsInvavid]);

  useEffect(() => {
    if (advancedOptions?.current) {
      advancedOptions.current.isExpandPanel = isExpandPanel;
    } else {
      advancedOptions.current = {
        isExpandPanel: isExpandPanel
      };
    }
  }, [isExpandPanel, advancedOptions]);

  useEffect(() => {
    if (advancedOptions?.current) {
      setMaxAreaValue(
        advancedOptions.current.maxEnquiryArea
          ? Number(advancedOptions.current.maxEnquiryArea)
          : MAX_AREA_STEP
      );
      setSplitEnquiries(
        advancedOptions?.current?.splitEnquiries ? true : false
      );
      setIsMaxAreaInvalid(Boolean(advancedOptions.current.isMaxAreaInvalid));
      setFrequency(String(advancedOptions.current.referralSummaryFrequency));
      setIsExpandPanel(Boolean(advancedOptions.current.isExpandPanel));
      setSummaryEmails(String(advancedOptions.current.referralSummaryEmail));
      if (advancedOptions.current.referralSummaryEmail) {
        const emails = advancedOptions.current.referralSummaryEmail
          ?.toString()
          .split(',');
        const valids = emails.map((email) => !Utils.isInValidEmail(email));
        setIsSummaryEmailsInvalid(valids.includes(false));
      } else {
        setIsSummaryEmailsInvalid(false);
      }
    }
    // eslint-disable-next-line
  }, [advancedOptions.current]);

  return (
    <ExpandableTile
      expanded={isExpandPanel}
      handleClick={() => setIsExpandPanel(!isExpandPanel)}
      onBeforeClick={(evt: any) => {
        const className = evt.target.className;
        if (
          typeof className === 'string' &&
          className.includes('bx--tile--is-expanded')
        ) {
          return false;
        }

        if (tileContentRef.current) {
          return !tileContentRef.current.contains(evt.target);
        }
        return true;
      }}
    >
      <TileAboveTheFoldContent>
        <div className="advanced-feature-title">Advanced features</div>
        <Link>Set up a maximum referral area and more</Link>
      </TileAboveTheFoldContent>
      <TileBelowTheFoldContent>
        <div
          ref={tileContentRef}
          className="advanced-feature-content-container"
        >
          <div className="advanced-feature-section">
            <div className="title">Maximum referral area</div>
            <div className="desc">
              Set an optional area for enquiry splitting
            </div>
            <FormGroup legendText="Maximum area">
              <RadioButtonGroup
                defaultSelected={splitEnquiries ? 'split' : 'none'}
                name="max-area-radio-button-group"
                valueSelected={splitEnquiries ? 'split' : 'none'}
                orientation="vertical"
                onChange={(value: string) => handleMaxAreaModeChange(value)}
              >
                <RadioButton
                  id="max-area-radio-1"
                  labelText="None - do not split enquiries (default)"
                  value="none"
                />
                <RadioButton
                  id="max-area-radio-2"
                  labelText="Split enquiries when area exceeds"
                  value="split"
                />
              </RadioButtonGroup>
            </FormGroup>
            {splitEnquiries && (
              <div className="max-area-input-container">
                <NumberInput
                  id="max-area-input"
                  helperText="0.1 — 19.0 km²"
                  max={MAX_AREA_MAX}
                  min={MAX_AREA_MIN}
                  step={MAX_AREA_STEP}
                  defaultValue={maxAreaValue > 0 ? maxAreaValue : undefined}
                  value={maxAreaValue > 0 ? maxAreaValue : ''}
                  invalid={isMaxAreaInvalid}
                  onChange={handleNumberInputChange}
                />
                <span>km²</span>
              </div>
            )}
          </div>
          <hr className="section__divider" />
          <div className="advanced-feature-section">
            <div className="title">Automatic email summary</div>
            <div className="desc">
              Recipients receive summary emails of the previous day’s referral
              activity. Choose a frequency to add recipients.
            </div>
            <FormGroup legendText="Frequency">
              <RadioButtonGroup
                defaultSelected={frequency}
                name="frequency-radio-button-group"
                valueSelected={frequency}
                orientation="vertical"
                onChange={(value: string) => handleFrequencyChange(value)}
              >
                <RadioButton
                  id="frequency-radio-1"
                  labelText="None"
                  value="NEVER"
                />
                <RadioButton
                  id="frequency-radio-2"
                  labelText="Daily"
                  value="DAILY"
                />
                <RadioButton
                  id="frequency-radio-3"
                  labelText="Weekly"
                  value="WEEKLY"
                />
              </RadioButtonGroup>
            </FormGroup>
            {frequency !== 'NEVER' && (
              <TextArea
                cols={10}
                helperText="Comma-separated email addresses"
                id="summary-emails"
                invalidText="A valid email addresses is required"
                invalid={isSummaryEmailsInvalid && isSummaryEmailsDirty}
                labelText="Add or remove recipients"
                rows={5}
                defaultValue={summaryEmails}
                onChange={handleSummaryEmailsChange}
              />
            )}
          </div>
        </div>
      </TileBelowTheFoldContent>
    </ExpandableTile>
  );
};

type Props = {
  advancedOptions: React.MutableRefObject<
    referralChannelAdvancedOptions | undefined
  >;
  setAdvancedOptionsInvavid: Function;
  setAdvancedOptionsDirty?: Function;
};

export default React.memo(AdvancedFeaturesTile);
